import React from "react";

import { getAllocationPercentages } from "../model/calculations";
import { formatMoney } from "../model/format";
import { getCombinedTarget } from "../model/targets";
import {
  AllocationLabels,
  BreakdownKey,
  BreakdownRow,
  MarketTarget,
} from "../model/types";
import ValueWithTarget from "./ValueWithTarget";

interface BreakdownChartProps {
  title: string;
  rows: BreakdownRow[];
  metric: "revenue" | "profit";
  allocationLabels: AllocationLabels;
  breakdownKey: BreakdownKey;
  targets: MarketTarget[];
  marketFilter: string[];
}

export default function BreakdownChart(props: BreakdownChartProps) {
  const {
    title,
    rows,
    metric,
    allocationLabels,
    breakdownKey,
    targets,
    marketFilter,
  } = props;
  const label = metric === "revenue" ? "Revenue" : "Profit";
  const sortedRows = [...rows].sort(
    (left, right) => right[metric] - left[metric],
  );
  const max = Math.max(...rows.map((row) => Math.abs(row[metric])), 1);
  // Each row is exactly one month here, so its target is unambiguous. Every other
  // breakdown (by Client, Market, Campaign...) has no per-row target to compare against -
  // targets are only ever set per market *and* month together.
  const isMonthBreakdown = breakdownKey === "month";

  const targetFor = (row: BreakdownRow): number | null =>
    isMonthBreakdown && row.rawValue !== "No date"
      ? getCombinedTarget(targets, [row.rawValue], marketFilter, metric)
      : null;

  const allocationPercentSummary = (row: BreakdownRow) => {
    const allocation = getAllocationPercentages(row.allocation[metric]);

    return `${allocationLabels.reportingDate} ${Math.round(
      allocation.reportingDate * 100,
    )}% · ${allocationLabels.budgetMonth} ${Math.round(
      allocation.budgetMonth * 100,
    )}%`;
  };

  const allocationValueSummary = (row: BreakdownRow, target: number | null) => {
    const allocation = row.allocation[metric];
    const summary = `${allocationLabels.reportingDate} ${formatMoney(
      allocation.reportingDate,
    )} · ${allocationLabels.budgetMonth} ${formatMoney(allocation.budgetMonth)}`;

    return target === null
      ? summary
      : `${summary} · Target ${formatMoney(target)}`;
  };

  const percent = (value: number) => `${Math.round(value * 100)}%`;

  return (
    <article className="finance-reporting-card finance-reporting-card--chart">
      <div className="finance-reporting-card__heading">
        <div>
          <p className="finance-reporting-eyebrow">Comparison</p>
          <h3>{title}</h3>
        </div>
        <div className="finance-reporting-legend">
          <span>
            <i className="finance-reporting-legend__ad-date" />
            {allocationLabels.reportingDate}
          </span>
          <span>
            <i className="finance-reporting-legend__budget-month" />
            {allocationLabels.budgetMonth}
          </span>
        </div>
      </div>

      {rows.length ? (
        <div className="finance-reporting-bars">
          {sortedRows.map((row) => {
            const allocation = getAllocationPercentages(row.allocation[metric]);
            const target = targetFor(row);
            // Relative to the target when this row has one (so the bar reads as progress
            // toward it, possibly under 100%); otherwise relative to the largest row, as
            // before. Either way, the two segments inside still split proportionally by
            // their own share of this row's total, via `allocation` above.
            const fillPercent = target
              ? Math.min((Math.abs(row[metric]) / target) * 100, 100)
              : (Math.abs(row[metric]) / max) * 100;

            return (
              <div
                key={row.label}
                className="finance-reporting-bars__row"
                title={`${row.label}: ${formatMoney(
                  row[metric],
                )} ${label.toLocaleLowerCase()}. ${allocationPercentSummary(row)}`}
              >
                <span className="finance-reporting-bars__label">
                  {row.label}
                </span>
                <div className="finance-reporting-bars__visual">
                  <div className="finance-reporting-bars__track">
                    <span
                      className="finance-reporting-bars__fill"
                      style={{
                        width: `${Math.max(fillPercent, row[metric] ? 1 : 0)}%`,
                      }}
                    >
                      <i
                        className="finance-reporting-bars__ad-date"
                        style={{ width: percent(allocation.reportingDate) }}
                      />
                      <i
                        className="finance-reporting-bars__budget-month"
                        style={{ width: percent(allocation.budgetMonth) }}
                      />
                    </span>
                  </div>
                  <span className="finance-reporting-bars__allocation">
                    {allocationValueSummary(row, target)}
                  </span>
                </div>
                <span className="finance-reporting-bars__value">
                  <ValueWithTarget
                    value={row[metric]}
                    target={target}
                    format={formatMoney}
                    className="finance-reporting-bars__value-target"
                  />
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="finance-reporting-empty">
          No eligible records for this configuration.
        </div>
      )}
    </article>
  );
}
